import { type KeyboardEvent, type ReactNode, useState } from 'react';
import { cn } from '@/lib/utils';

export interface HeaderTabItemV2 {
  id: string;
  label: string;
  component: ReactNode;
}

interface HeaderTabsV2Props {
  className?: string;
  tabs: HeaderTabItemV2[];
  defaultTabIndex?: number;
  onTabChange?: (tabId: string) => void;
  actionButtons?: ReactNode;
}

export const HeaderTabsV2 = ({
  tabs,
  defaultTabIndex = 0,
  onTabChange,
  className,
  actionButtons
}: HeaderTabsV2Props) => {
  const [currentTab, setCurrentTab] = useState(defaultTabIndex);

  const selectTab = (tabIndex: number) => {
    if (!tabs[tabIndex]) {
      return;
    }

    setCurrentTab(tabIndex);
    onTabChange?.(tabs[tabIndex].id);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tabIndex: number) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      selectTab((tabIndex + 1) % tabs.length);
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      selectTab((tabIndex - 1 + tabs.length) % tabs.length);
    }
  };

  return (
    <div className={className}>
      <div className='flex items-center justify-between border-b border-gray-200 bg-muted'>
        <div role='tablist' className='relative flex h-8 items-stretch overflow-x-auto bg-muted'>
          {tabs.map((tab, tabIndex) => (
            <button
              key={tab.id}
              type='button'
              role='tab'
              aria-selected={tabs[currentTab]?.id === tab.id}
              className={cn(
                'relative inline-flex h-8 min-w-20 items-center justify-center rounded-none border-0 bg-transparent px-4 py-0 text-[12px] font-semibold leading-none text-slate-500 transition-colors duration-300 after:absolute after:bottom-0 after:left-0 after:h-[3px] after:w-full after:bg-main after:opacity-0 hover:text-main hover:after:opacity-100',
                tabs[currentTab]?.id === tab.id && 'bg-main/10 text-main after:opacity-100'
              )}
              onClick={() => selectTab(tabIndex)}
              onKeyDown={event => handleKeyDown(event, tabIndex)}
            >
              <span className='translate-y-px'>{tab.label}</span>
            </button>
          ))}
        </div>
        <div className='flex items-center'>{actionButtons}</div>
      </div>

      <div>{tabs[currentTab]?.component}</div>
    </div>
  );
};

export default HeaderTabsV2;
